import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Phone, Menu, X, ShoppingCart } from 'lucide-react';
import { Button } from './ui/button';
import { LanguageSwitcher } from './LanguageSwitcher';
import { useLanguage } from '../contexts/LanguageContext';
import { useCart } from '../contexts/CartContext';
import { useShopConfig } from '../contexts/ShopConfigContext';

const SECTIONS = ['home', 'services', 'about', 'contact'];

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { t } = useLanguage();
  const { count } = useCart();
  const { live } = useShopConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const onShop = location.pathname.startsWith('/shop') || ['/cart', '/checkout'].includes(location.pathname);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    setIsMobileMenuOpen(false);
    if (location.pathname !== '/') {
      navigate(`/#${id}`);
      return;
    }
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const navClass = 'text-gray-700 hover:text-pink-600 font-medium transition-colors';

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-white'}`}>
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center space-x-3" data-testid="header-logo">
            <div className="w-12 h-12 bg-gradient-to-br from-pink-400 to-pink-500 rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-xl">CH</span>
            </div>
            <div>
              <h1 className="font-bold text-xl text-gray-900">Clean Home</h1>
              <p className="text-xs text-gray-600">Peterborough</p>
            </div>
          </Link>

          <nav className="hidden md:flex items-center space-x-8">
            {SECTIONS.map(id => (
              <button key={id} onClick={() => scrollToSection(id)} className={navClass} data-testid={`nav-${id}`}>{t(id)}</button>
            ))}
            <Link to="/shop" className={`${navClass} ${onShop ? 'text-pink-600' : ''}`} data-testid="nav-shop">{t('shop')}</Link>
          </nav>

          <div className="hidden md:flex items-center space-x-4">
            <LanguageSwitcher />
            {live && (
              <Link to="/cart" className="relative p-2 text-gray-700 hover:text-pink-600" aria-label="Cart" data-testid="nav-cart">
                <ShoppingCart className="w-6 h-6" />
                {count > 0 && <span className="absolute -top-1 -right-1 bg-pink-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center" data-testid="cart-count">{count}</span>}
              </Link>
            )}
            <Button className="bg-pink-500 hover:bg-pink-600 text-white" onClick={() => window.open('tel:+447842860575', '_self')} data-testid="header-call-button">
              <Phone className="w-4 h-4 mr-2" />
              {t('callNow')}
            </Button>
          </div>

          <div className="md:hidden flex items-center gap-2">
            {live && (
              <Link to="/cart" className="relative p-2 text-gray-700" aria-label="Cart" data-testid="nav-cart-mobile">
                <ShoppingCart className="w-6 h-6" />
                {count > 0 && <span className="absolute -top-0.5 -right-0.5 bg-pink-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">{count}</span>}
              </Link>
            )}
            <button className="text-gray-700 p-2" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Menu" data-testid="mobile-menu-button">
              {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden py-4 border-t" data-testid="mobile-menu">
            <nav className="flex flex-col space-y-4">
              {SECTIONS.map(id => (
                <button key={id} onClick={() => scrollToSection(id)} className={`${navClass} text-left`} data-testid={`mobile-nav-${id}`}>{t(id)}</button>
              ))}
              <Link to="/shop" onClick={() => setIsMobileMenuOpen(false)} className={navClass} data-testid="mobile-nav-shop">{t('shop')}</Link>
              <div className="flex items-center justify-between">
                <LanguageSwitcher />
              </div>
              <Button className="bg-pink-500 hover:bg-pink-600 text-white w-full" onClick={() => window.open('tel:+447842860575', '_self')} data-testid="mobile-call-button">
                <Phone className="w-4 h-4 mr-2" />
                {t('callNow')}
              </Button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};
